import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
} from "react";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { createPortal } from "react-dom";
import {
  BarChart3,
  Bell,
  BookOpen,
  CreditCard,
  Info,
  LayoutDashboard,
  LifeBuoy,
  Menu,
  MonitorSmartphone,
  Rocket,
  ScrollText,
  Search,
  Settings,
  School,
  TrendingUp,
  Users,
  type LucideIcon,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const icons = {
  "bar-chart": BarChart3,
  bell: Bell,
  "book-open": BookOpen,
  "credit-card": CreditCard,
  info: Info,
  "layout-dashboard": LayoutDashboard,
  "life-buoy": LifeBuoy,
  menu: Menu,
  monitor: MonitorSmartphone,
  rocket: Rocket,
  school: School,
  "scroll-text": ScrollText,
  search: Search,
  settings: Settings,
  "trending-up": TrendingUp,
  users: Users,
} satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof icons;

export function BrandMark({ className = "" }: { className?: string }) {
  const gradientId = useId().replaceAll(":", "");
  return (
    <svg
      aria-hidden="true"
      className={`kt-brand-mark ${className}`.trim()}
      focusable="false"
      viewBox="0 0 64 64"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#e8e7ff" />
          <stop offset="1" stopColor="#cff5ea" />
        </linearGradient>
      </defs>
      <rect width="64" height="64" rx="19" fill={`url(#${gradientId})`} />
      <path d="M13 25 16 8l12 12zm38 0L48 8 36 20z" fill="#433d9e" />
      <rect x="10" y="15" width="44" height="40" rx="20" fill="#5751b9" />
      <ellipse cx="25" cy="33" rx="10" ry="12" fill="#fffcf2" />
      <ellipse cx="39" cy="33" rx="10" ry="12" fill="#fffcf2" />
      <circle cx="26" cy="33" r="3.7" fill="#17243e" />
      <circle cx="38" cy="33" r="3.7" fill="#17243e" />
      <circle cx="27.3" cy="31.5" r="1.1" fill="#fff" />
      <circle cx="39.3" cy="31.5" r="1.1" fill="#fff" />
      <path d="m27.5 39 9 0-4.5 6z" fill="#ffb84d" />
      <path d="M20 48c5 8 19 8 24 0-6 2-18 2-24 0" fill="#75d9bf" />
    </svg>
  );
}

export function Icon({
  name,
  size = 20,
  strokeWidth = 1.9,
}: {
  name: IconName;
  size?: number;
  strokeWidth?: number;
}) {
  const IconComponent = icons[name];
  return (
    <IconComponent
      aria-hidden="true"
      focusable="false"
      size={size}
      strokeWidth={strokeWidth}
    />
  );
}

export function Tooltip({
  label,
  children,
}: PropsWithChildren<{ label: string }>) {
  const tooltipId = useId();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);

  function openTooltip() {
    const bounds = anchorRef.current?.getBoundingClientRect();
    if (!bounds) return;
    const tooltipHalfWidth = Math.min(160, (window.innerWidth - 24) / 2);
    const centeredLeft = bounds.left + bounds.width / 2;
    setPosition({
      left: Math.min(
        Math.max(centeredLeft, tooltipHalfWidth + 12),
        window.innerWidth - tooltipHalfWidth - 12,
      ),
      top: bounds.top - 8,
    });
  }

  return (
    <>
      <span
        ref={anchorRef}
        className="kt-tooltip-anchor"
        aria-describedby={position ? tooltipId : undefined}
        onMouseEnter={openTooltip}
        onMouseLeave={() => setPosition(null)}
        onFocus={openTooltip}
        onBlur={() => setPosition(null)}
      >
        {children}
      </span>
      {position
        ? createPortal(
            <span
              id={tooltipId}
              className="kt-tooltip"
              role="tooltip"
              style={position}
            >
              {label}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "quiet" | "danger";
};

export function Button({
  className = "",
  variant = "primary",
  ...props
}: ButtonProps) {
  return (
    <button
      className={`kt-button kt-button--${variant} ${className}`.trim()}
      {...props}
    />
  );
}

type LinkButtonProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: "primary" | "secondary" | "quiet";
};

export function LinkButton({
  className = "",
  variant = "primary",
  ...props
}: LinkButtonProps) {
  return (
    <a
      className={`kt-button kt-button--${variant} ${className}`.trim()}
      {...props}
    />
  );
}

export function Card({
  children,
  className = "",
}: PropsWithChildren<{ className?: string }>) {
  return (
    <section className={`kt-card ${className}`.trim()}>{children}</section>
  );
}

const modalFocusableSelector = [
  "button:not([disabled])",
  "[href]",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * Gives custom and native modal dialogs consistent keyboard behaviour.
 * The opener is restored after close, while Tab stays within the active modal.
 */
export function useModalDialog<T extends HTMLElement = HTMLElement>({
  open = true,
  onClose,
  busy = false,
}: {
  open?: boolean;
  onClose: () => void;
  busy?: boolean;
}) {
  const dialogRef = useRef<T>(null);
  const onCloseRef = useRef(onClose);
  const busyRef = useRef(busy);
  const openerRef = useRef<HTMLElement | null>(null);

  if (
    open &&
    openerRef.current === null &&
    typeof document !== "undefined" &&
    document.activeElement instanceof HTMLElement
  ) {
    openerRef.current = document.activeElement;
  }

  useEffect(() => {
    onCloseRef.current = onClose;
    busyRef.current = busy;
  }, [busy, onClose]);

  useEffect(() => {
    if (!open) {
      openerRef.current = null;
      return;
    }

    const opener = openerRef.current;

    const focusableElements = () =>
      Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          modalFocusableSelector,
        ) ?? [],
      ).filter(
        (element) =>
          !element.hasAttribute("hidden") &&
          element.getClientRects().length > 0,
      );

    const focusFrame = window.requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      const target =
        dialog?.querySelector<HTMLElement>("[autofocus]") ??
        focusableElements()[0] ??
        dialog;
      target?.focus();
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (!busyRef.current) {
          event.preventDefault();
          onCloseRef.current();
        }
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const dialog = dialogRef.current;
      const focusable = focusableElements();
      if (!dialog || focusable.length === 0) {
        event.preventDefault();
        dialog?.focus();
        return;
      }

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      const focusIsOutside =
        !(active instanceof Node) || !dialog.contains(active);

      if (event.shiftKey && (active === first || focusIsOutside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || focusIsOutside)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown);
      window.requestAnimationFrame(() => {
        if (opener?.isConnected) {
          opener.focus();
        }
      });
    };
  }, [open]);

  return dialogRef;
}

export function ResponsiveTable({
  children,
  className = "",
  label,
  showHint = true,
  fullBleed = true,
}: PropsWithChildren<{
  className?: string;
  label: string;
  showHint?: boolean;
  fullBleed?: boolean;
}>) {
  const regionRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const table = regionRef.current?.querySelector(":scope > table");
    if (!(table instanceof HTMLTableElement)) return;
    const headers = Array.from(table.tHead?.rows[0]?.cells ?? []).map((cell) =>
      (cell.textContent ?? "").replace(/\s+/g, " ").trim(),
    );

    for (const body of Array.from(table.tBodies)) {
      for (const row of Array.from(body.rows)) {
        for (const [index, cell] of Array.from(row.cells).entries()) {
          const spansTable = cell.colSpan > 1;
          cell.dataset.label = spansTable ? "" : (headers[index] ?? "");
          if (spansTable) {
            cell.dataset.tableSpan = "true";
          } else {
            delete cell.dataset.tableSpan;
          }
        }
      }
    }
  });

  return (
    <div
      ref={regionRef}
      className={`kt-responsive-table kt-responsive-table--sticky-first ${fullBleed ? "kt-responsive-table--full-bleed" : ""} ${className}`.trim()}
      role="region"
      aria-label={label}
      tabIndex={0}
    >
      {children}
      {showHint ? (
        <span className="kt-responsive-table__hint">
          Na węższym ekranie każdy wiersz jest pokazany jako czytelna karta.
        </span>
      ) : null}
    </div>
  );
}

export function StatGrid({
  children,
  compact = false,
  className = "",
  ...props
}: PropsWithChildren<
  { compact?: boolean; className?: string } & HTMLAttributes<HTMLElement>
>) {
  return (
    <section
      {...props}
      className={`kt-stat-grid ${compact ? "kt-stat-grid--compact" : ""} ${className}`.trim()}
    >
      {children}
    </section>
  );
}

export function FilterBar({
  children,
  className = "",
  label = "Filtry tabeli",
}: PropsWithChildren<{ className?: string; label?: string }>) {
  return (
    <div
      className={`kt-filter-bar ${className}`.trim()}
      role="group"
      aria-label={label}
    >
      {children}
    </div>
  );
}

export function DataTablePanel({
  children,
  className = "",
}: PropsWithChildren<{ className?: string }>) {
  return (
    <Card className={`kt-data-table-panel ${className}`.trim()}>
      {children}
    </Card>
  );
}

export function PageStack({
  children,
  className = "",
  ...props
}: PropsWithChildren<{ className?: string } & HTMLAttributes<HTMLDivElement>>) {
  return (
    <div {...props} className={`kt-page-stack ${className}`.trim()}>
      {children}
    </div>
  );
}

export function Badge({
  children,
  tone = "neutral",
}: PropsWithChildren<{
  tone?: "neutral" | "success" | "warning" | "danger" | "info";
}>) {
  return <span className={`kt-badge kt-badge--${tone}`}>{children}</span>;
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="kt-page-header">
      <div>
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? (
        <div className="kt-page-header__actions">{actions}</div>
      ) : null}
    </header>
  );
}

export function StatCard({
  label,
  value,
  detail,
  href,
}: {
  label: string;
  value: string;
  detail: string;
  href?: string;
}) {
  const content = (
    <>
      <span className="kt-stat__label">{label}</span>
      <strong className="kt-stat__value">{value}</strong>
      <span className="kt-stat__detail">{detail}</span>
    </>
  );

  return href ? (
    <a className="kt-card kt-stat kt-stat--link" href={href}>
      {content}
    </a>
  ) : (
    <div className="kt-card kt-stat">{content}</div>
  );
}

export type TwoFactorSetupResult = {
  totpURI: string;
  backupCodes: string[];
};

function operationError(error: unknown) {
  return error instanceof Error
    ? error.message
    : "Nie udało się wykonać operacji. Spróbuj ponownie.";
}

export function TwoFactorSettings({
  enabled,
  onEnable,
  onVerify,
  onDisable,
  onActivated,
}: {
  enabled: boolean;
  onEnable: (password: string) => Promise<TwoFactorSetupResult>;
  onVerify: (code: string) => Promise<void>;
  onDisable: (password: string) => Promise<void>;
  onActivated?: () => void;
}) {
  const [active, setActive] = useState(enabled);
  const [setup, setSetup] = useState<TwoFactorSetupResult | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => setActive(enabled), [enabled]);
  let manualSecret = "";
  if (setup) {
    try {
      manualSecret = new URL(setup.totpURI).searchParams.get("secret") ?? "";
    } catch {
      manualSecret = "";
    }
  }

  async function enable(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      const values = new FormData(event.currentTarget);
      setSetup(await onEnable(String(values.get("password") ?? "")));
    } catch (caught) {
      setError(operationError(caught));
    } finally {
      setPending(false);
    }
  }

  async function verify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      const values = new FormData(event.currentTarget);
      await onVerify(String(values.get("code") ?? ""));
      setSetup(null);
      setActive(true);
      onActivated?.();
    } catch (caught) {
      setError(operationError(caught));
    } finally {
      setPending(false);
    }
  }

  async function disable(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      const values = new FormData(event.currentTarget);
      await onDisable(String(values.get("password") ?? ""));
      setActive(false);
      setSetup(null);
    } catch (caught) {
      setError(operationError(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="kt-two-factor" aria-labelledby="two-factor-title">
      <div className="kt-two-factor__heading">
        <div>
          <span className="kt-two-factor__eyebrow">
            Dodatkowe zabezpieczenie konta
          </span>
          <h3 id="two-factor-title">Weryfikacja dwuetapowa (2FA)</h3>
          <p>
            Po wpisaniu hasła poprosimy także o jednorazowy kod z aplikacji
            uwierzytelniającej.
          </p>
        </div>
        <div className="kt-two-factor__status" aria-label="Stan weryfikacji">
          <span>Stan</span>
          <Badge tone={active ? "success" : "warning"}>
            {active ? "Weryfikacja włączona" : "Weryfikacja wyłączona"}
          </Badge>
        </div>
      </div>
      {error ? (
        <p className="kt-two-factor__error" role="alert">
          {error}
        </p>
      ) : null}
      {!active && !setup ? (
        <div className="kt-two-factor__action-panel">
          <div>
            <strong>Włącz dodatkową ochronę logowania</strong>
            <p>
              Potwierdź hasło, a następnie zeskanuj kod QR i wpisz kod z
              aplikacji. Weryfikacja zostanie włączona dopiero po wykonaniu
              wszystkich kroków.
            </p>
          </div>
          <form
            className="kt-two-factor__form"
            onSubmit={(event) => void enable(event)}
          >
            <label>
              Potwierdź aktualnym hasłem
              <input
                name="password"
                type="password"
                autoComplete="current-password"
                required
              />
            </label>
            <Button type="submit" disabled={pending}>
              {pending ? "Przygotowywanie…" : "Włącz weryfikację dwuetapową"}
            </Button>
          </form>
        </div>
      ) : null}
      {!active && setup ? (
        <div className="kt-two-factor__setup">
          <p>
            Zeskanuj kod QR w aplikacji uwierzytelniającej, a następnie wpisz
            wygenerowany kod.
          </p>
          <div className="kt-two-factor__enrollment">
            <figure className="kt-two-factor__qr">
              <QRCodeSVG
                value={setup.totpURI}
                size={224}
                level="M"
                marginSize={2}
                bgColor="#ffffff"
                fgColor="#111827"
                title="Kod QR do konfiguracji weryfikacji dwuetapowej"
              />
              <figcaption>Zeskanuj kod aparatem w aplikacji.</figcaption>
            </figure>
            <div className="kt-two-factor__manual-key">
              <strong>Nie możesz zeskanować kodu?</strong>
              <p>
                Wpisz poniższy klucz ręcznie w aplikacji uwierzytelniającej.
              </p>
              <code>{manualSecret}</code>
            </div>
          </div>
          <form
            className="kt-two-factor__form"
            onSubmit={(event) => void verify(event)}
          >
            <label>
              Kod jednorazowy
              <input
                name="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                minLength={6}
                maxLength={6}
                required
              />
            </label>
            <Button type="submit" disabled={pending}>
              {pending ? "Sprawdzanie…" : "Potwierdź i włącz"}
            </Button>
          </form>
          <div className="kt-two-factor__backup">
            <strong>Zapisz kody zapasowe w bezpiecznym miejscu</strong>
            <p>
              Każdy kod działa tylko raz. Nie zapisuj ich na współdzielonym
              urządzeniu.
            </p>
            <ul>
              {setup.backupCodes.map((code) => (
                <li key={code}>
                  <code>{code}</code>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
      {active ? (
        <div className="kt-two-factor__action-panel kt-two-factor__action-panel--active">
          <div>
            <strong>Twoje konto ma dodatkową ochronę</strong>
            <p>
              Przy następnym logowaniu po haśle podasz kod z aplikacji
              uwierzytelniającej albo jednorazowy kod odzyskiwania.
            </p>
          </div>
          <form
            className="kt-two-factor__form"
            onSubmit={(event) => void disable(event)}
          >
            <label>
              Potwierdź hasłem, aby wyłączyć
              <input
                name="password"
                type="password"
                autoComplete="current-password"
                required
              />
            </label>
            <Button type="submit" variant="danger" disabled={pending}>
              {pending ? "Wyłączanie…" : "Wyłącz weryfikację dwuetapową"}
            </Button>
          </form>
        </div>
      ) : null}
    </section>
  );
}

export function TwoFactorChallenge({
  onVerifyCode,
  onVerifyBackupCode,
  onVerified,
}: {
  onVerifyCode: (code: string, trustDevice: boolean) => Promise<void>;
  onVerifyBackupCode: (code: string, trustDevice: boolean) => Promise<void>;
  onVerified: () => void;
}) {
  const [backupMode, setBackupMode] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      const values = new FormData(event.currentTarget);
      const code = String(values.get("code") ?? "").trim();
      await (backupMode
        ? onVerifyBackupCode(code, trustDevice)
        : onVerifyCode(code, trustDevice));
      onVerified();
    } catch (caught) {
      setError(operationError(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="kt-two-factor kt-two-factor--challenge">
      <h1>Potwierdź logowanie</h1>
      <p>
        {backupMode
          ? "Wpisz jeden z zapisanych kodów zapasowych."
          : "Wpisz sześciocyfrowy kod z aplikacji uwierzytelniającej."}
      </p>
      <form
        className="kt-two-factor__form"
        onSubmit={(event) => void submit(event)}
      >
        <label>
          {backupMode ? "Kod zapasowy" : "Kod jednorazowy"}
          <input
            name="code"
            inputMode={backupMode ? "text" : "numeric"}
            autoComplete="one-time-code"
            pattern={backupMode ? undefined : "[0-9]{6}"}
            minLength={backupMode ? 6 : 6}
            maxLength={backupMode ? 64 : 6}
            autoFocus
            required
          />
        </label>
        {error ? (
          <p className="kt-two-factor__error" role="alert">
            {error}
          </p>
        ) : null}
        <label className="kt-two-factor__trusted-device">
          <input
            type="checkbox"
            checked={trustDevice}
            onChange={(event) => setTrustDevice(event.currentTarget.checked)}
          />
          <span>
            <strong>Zapamiętaj to urządzenie przez 30 dni</strong>
            <small>
              Włącz tylko na prywatnym komputerze. Nie używaj na urządzeniu
              współdzielonym.
            </small>
          </span>
        </label>
        <Button type="submit" disabled={pending}>
          {pending ? "Sprawdzanie…" : "Potwierdź"}
        </Button>
        <Button
          type="button"
          variant="quiet"
          onClick={() => {
            setBackupMode((value) => !value);
            setError("");
          }}
        >
          {backupMode ? "Użyj kodu z aplikacji" : "Użyj kodu zapasowego"}
        </Button>
      </form>
    </section>
  );
}
